import {
  Flex,
  Card,
  Dropdown,
  DropdownOption,
  ChartSkeleton,
  FlexProps,
  DisplaySwitch
} from '@blockanalitica/ui';
import type { SimpleChartData } from '@blockanalitica/ui/package/types/src/components/charts/SimpleChart';
import { useCallback, useMemo, useState } from 'react';
import { useApi } from '@/api';
import { ChartTimeFilter } from '@/components';
import HistoricRatesGraphs from './HistoricRatesGraphs';

interface HistoricRatesGraphsProps extends FlexProps {
  marketAddress: string;
}

type MarketHistoricData = {
  results: SimpleChartData[];
  symbol: string;
  name: string;
};

const APY_OPTIONS = [
  {
    value: '_1d',
    label: '1D AVG APY'
  },
  {
    value: '_7d',
    label: '7D AVG APY'
  },
  {
    value: '_30d',
    label: '30D AVG APY'
  },
  {
    value: '_90d',
    label: '90D AVG APY'
  },
  {
    value: '_365d',
    label: '1Y AVG APY'
  }
];

export default function HistoricRatesTabs({
  marketAddress,
  ...rest
}: HistoricRatesGraphsProps) {
  const [activeDisplayOption, setActiveDisplayOption] = useState('apys');

  const [rateType, setRateType] = useState('_1d');
  const setRateTypeCallback = useCallback((value: string | string[]) => {
    return setRateType(value as string);
  }, []);

  const [chartDaysAgo, setChartDaysAgo] = useState('30');

  const displayOptions = useMemo(
    () => [
      { key: 'apys', value: 'Rates' },
      { key: 'utilization', value: 'Utilization' },
      { key: 'price', value: 'Price' }
    ],
    []
  );

  const [data, error] = useApi<MarketHistoricData>({
    url: `/markets/${marketAddress}/historic/`,
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return (
      <Flex flexDirection="column" gap="xs" {...rest}>
        <ChartSkeleton />
      </Flex>
    );
  }

  return (
    <Card flexDirection="column" gap="xs" {...rest}>
      <Flex
        justifyContent="space-between"
        alignItems={['center', 'flex-start']}
        flexDirection={['column', 'row']}
        gap={['xs', 'm']}>
        <DisplaySwitch
          data={displayOptions}
          activeOption={activeDisplayOption}
          onChange={setActiveDisplayOption}
        />
        <Flex
          gap={['xs', 'm']}
          justifyContent="flex-end"
          alignItems="center"
          flexDirection={['row', 'row']}>
          {activeDisplayOption === 'apys' ? (
            <Dropdown onChange={setRateTypeCallback} initialValue={rateType}>
              {APY_OPTIONS.map((option, index) => (
                <DropdownOption value={option.value} key={index}>
                  {option.label}
                </DropdownOption>
              ))}
            </Dropdown>
          ) : null}
          <ChartTimeFilter
            activeOption={chartDaysAgo}
            onChange={setChartDaysAgo}
          />
        </Flex>
      </Flex>
      <HistoricRatesGraphs
        rateType={rateType}
        activeDisplayOption={activeDisplayOption}
        chartDaysAgo={chartDaysAgo}
        data={data.results}
      />
    </Card>
  );
}
